import { Component } from 'react';
import { captureErrorReplay } from '../lib/replay';
import { initBreadcrumbs, getBreadcrumbs, teardownBreadcrumbs } from './breadcrumbs';
import { SERVER_URL } from '../utils/apiUtilsConstants';
import { SUPPORT_EMAIL } from '@/shared/adapters/constants';

const LOG_LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };

let _config = { logToken: '', appName: '', environment: 'development' };
let _user = null;
let _queue = [];
let _flushTimer = null;
const FLUSH_INTERVAL = 2000;
const FLUSH_SIZE = 20;
const MIN_LEVEL = LOG_LEVELS.debug;

let _loggingEnabled = true;

function flush() {
  try {
    if (!_loggingEnabled) return;
    if (_queue.length === 0 || !_config.logToken) return;

    const batch = _queue.splice(0, _queue.length);

    fetch(`${SERVER_URL}/api/log/ingest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${_config.logToken}`,
      },
      body: JSON.stringify({ batch }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never let flush crash the app
  }
}

function enqueue(entry) {
  try {
    if (!_loggingEnabled) return;
    _queue.push(entry);
    if (_queue.length >= FLUSH_SIZE) {
      clearTimeout(_flushTimer);
      flush();
    } else if (!_flushTimer) {
      _flushTimer = setTimeout(() => {
        _flushTimer = null;
        flush();
      }, FLUSH_INTERVAL);
    }
  } catch {
    // Never let enqueue crash the app
  }
}

function log(level, message, metadata = {}) {
  try {
    if (!_loggingEnabled) return;
    if (LOG_LEVELS[level] < MIN_LEVEL) return;

    const isErrorLevel = level === 'error' || level === 'warn';
    const breadcrumbs = isErrorLevel ? getBreadcrumbs() : undefined;

    let replayId = metadata.replay_id;
    if (level === 'error' && !replayId) {
      replayId = captureErrorReplay();
    }

    enqueue({
      level,
      message,
      timestamp: new Date().toISOString(),
      app_name: _config.appName,
      environment: _config.environment,
      source: 'client',
      user_id: _user?.id || null,
      user_email: _user?.email || null,
      user_name: _user?.name || null,
      breadcrumbs: breadcrumbs && breadcrumbs.length > 0 ? breadcrumbs : undefined,
      metadata: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...metadata,
        replay_id: replayId || metadata.replay_id || undefined,
      },
    });
  } catch {
    // Never let log crash the app
  }
}

export const clientLogger = {
  debug: (msg, meta) => log('debug', msg, meta),
  info: (msg, meta) => log('info', msg, meta),
  warn: (msg, meta) => log('warn', msg, meta),
  error: (msg, meta) => log('error', msg, meta),
  setUser: (user) => {
    _user = user ? { id: user.id, email: user.email, name: user.name } : null;
  },
  clearUser: () => {
    _user = null;
  },
};

const REACT_APP_ENV = typeof process !== 'undefined' ? process.env.REACT_APP_ENV : '';
const IS_DEV = !REACT_APP_ENV || REACT_APP_ENV === 'development';

const REFRESH_STORAGE_KEY = '__logger_boundary_refresh_count';
const MAX_AUTO_REFRESHES = 2;

function getRefreshCount() {
  try {
    return Number(sessionStorage.getItem(REFRESH_STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
}

function setRefreshCount(n) {
  try {
    sessionStorage.setItem(REFRESH_STORAGE_KEY, String(n));
  } catch {
    // sessionStorage may be unavailable (e.g. private browsing quota)
  }
}

function clearRefreshCount() {
  try {
    sessionStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch {
  }
}

function buildErrorReport(error, errorInfo) {
  const parts = [];

  if (error) {
    const title = (error.name ? `${error.name}: ` : '') + (error.message || String(error));
    parts.push(title);

    if (error.stack) {
      parts.push('');
      parts.push('Stack trace:');
      parts.push(error.stack);
    }
  }

  if (errorInfo?.componentStack) {
    parts.push('');
    parts.push('Component stack:');
    parts.push(errorInfo.componentStack.trim());
  }

  parts.push('');
  parts.push(`URL: ${window.location.href}`);
  parts.push(`Time: ${new Date().toISOString()}`);

  return parts.join('\n');
}

const styles = {
  backdrop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '2rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    backgroundColor: '#f9fafb',
    color: '#1a1a1a',
  },
  card: (wide) => ({
    maxWidth: wide ? 720 : 480,
    width: '100%',
    background: '#ffffff',
    borderRadius: 16,
    border: '1px solid #e5e7eb',
    padding: '3rem 2.5rem',
    textAlign: 'center',
  }),
  iconCircle: (bg) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 72,
    height: 72,
    borderRadius: '50%',
    backgroundColor: bg,
    marginBottom: 24,
  }),
  heading: {
    fontSize: 22,
    fontWeight: 600,
    margin: '0 0 8px',
    color: '#111827',
  },
  subheading: {
    fontSize: 14,
    color: '#6b7280',
    margin: '0 0 32px',
    lineHeight: 1.6,
    maxWidth: 380,
    display: 'inline-block',
  },
  devSubheading: {
    fontSize: 13,
    color: '#9ca3af',
    margin: '0 0 24px',
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 48,
    padding: '0 32px',
    fontSize: 15,
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'background-color 150ms ease, transform 100ms ease',
    boxShadow: '0 1px 3px rgba(37,99,235,0.18), 0 1px 2px rgba(37,99,235,0.10)',
    marginBottom: 20,
  },
  copyButton: (copied) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 36,
    padding: '0 16px',
    fontSize: 13,
    fontWeight: 500,
    color: copied ? '#059669' : '#6b7280',
    backgroundColor: copied ? '#ecfdf5' : '#f9fafb',
    border: `1px solid ${copied ? '#a7f3d0' : '#e5e7eb'}`,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 150ms ease',
    marginBottom: 24,
  }),
  supportText: {
    fontSize: 13,
    color: '#9ca3af',
    margin: 0,
    lineHeight: 1.6,
  },
  supportLink: {
    color: '#2563eb',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 500,
  },
  errorContainer: {
    width: '100%',
    textAlign: 'left',
    marginBottom: 24,
    borderRadius: 10,
    overflow: 'hidden',
    border: '1px solid #fecaca',
  },
  errorHeader: {
    backgroundColor: '#fef2f2',
    padding: '14px 18px',
    fontSize: 13,
    fontWeight: 600,
    color: '#991b1b',
    borderBottom: '1px solid #fecaca',
    wordBreak: 'break-word',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  errorStack: {
    margin: 0,
    padding: '16px 18px',
    fontSize: 12,
    lineHeight: 1.7,
    color: '#4b5563',
    backgroundColor: '#ffffff',
    overflowX: 'auto',
    maxHeight: 220,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  componentStackContainer: {
    width: '100%',
    textAlign: 'left',
    marginBottom: 28,
    borderRadius: 10,
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  componentStackHeader: {
    backgroundColor: '#f9fafb',
    padding: '10px 18px',
    fontSize: 11,
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb',
  },
  componentStack: {
    margin: 0,
    padding: '16px 18px',
    fontSize: 12,
    lineHeight: 1.7,
    color: '#9ca3af',
    backgroundColor: '#ffffff',
    overflowX: 'auto',
    maxHeight: 180,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
};

// SVG icons rendered inline so the fallback has zero network deps.
const WarningIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const BugIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="6" width="8" height="14" rx="4" />
    <path d="M19 12h2" /><path d="M3 12h2" />
    <path d="M18 7.5l1.5-1.5" /><path d="M4.5 6L6 7.5" />
    <path d="M18 16.5l1.5 1.5" /><path d="M4.5 18L6 16.5" />
    <line x1="12" y1="6" x2="12" y2="2" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

class LoggerBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, supportEmail: null, copied: false };
    this._copyTimer = null;
  }

  componentDidMount() {
    const enabled = this.props.enabled !== false;
    _loggingEnabled = enabled;

    if (!enabled) return;

    try {
      const { logToken, appName, environment, user } = this.props;

      _config = {
        logToken: logToken || _config.logToken,
        appName: appName || _config.appName,
        environment: environment || _config.environment,
      };

      if (user) {
        clientLogger.setUser(user);
      }

      initBreadcrumbs();
    } catch {
      // Never let logger init crash the app
    }

    clearRefreshCount();

    if (!IS_DEV) {
      fetch(`${SERVER_URL}/api/ui/system-settings/support-email`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.email) this.setState({ supportEmail: data.email });
        })
        .catch(() => {});
    }

    this._caughtByReact = false;

    this._onError = (message, source, lineno, colno, error) => {
      if (this._caughtByReact) {
        this._caughtByReact = false;
        return;
      }

      try {
        const replayId = captureErrorReplay();
        clientLogger.error(typeof message === 'string' ? message : 'Unknown error', {
          replay_id: replayId,
          source,
          lineno,
          colno,
          stack: error?.stack || null,
          url: window.location.href,
        });
      } catch {
        // Never let error handler crash the app
      }
    };

    this._onRejection = (event) => {
      try {
        const replayId = captureErrorReplay();
        const err =
          event.reason instanceof Error ? event.reason : new Error(String(event.reason));
        clientLogger.error(`Unhandled Rejection: ${err.message}`, {
          replay_id: replayId,
          stack: err.stack || null,
          url: window.location.href,
        });
      } catch {
        // Never let rejection handler crash the app
      }
    };

    window.onerror = this._onError;
    window.addEventListener('unhandledrejection', this._onRejection);

    this._onUnload = () => flush();
    window.addEventListener('beforeunload', this._onUnload);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.enabled !== this.props.enabled) {
      _loggingEnabled = this.props.enabled !== false;
    }

    if (!_loggingEnabled) return;

    if (prevProps.logToken !== this.props.logToken && this.props.logToken) {
      _config.logToken = this.props.logToken;
    }
    if (prevProps.user !== this.props.user) {
      if (this.props.user) {
        clientLogger.setUser(this.props.user);
      } else {
        clientLogger.clearUser();
      }
    }
  }

  componentWillUnmount() {
    if (this._onError) {
      window.onerror = null;
    }
    if (this._onRejection) {
      window.removeEventListener('unhandledrejection', this._onRejection);
    }
    if (this._onUnload) {
      window.removeEventListener('beforeunload', this._onUnload);
    }
    if (this._copyTimer) {
      clearTimeout(this._copyTimer);
    }
    if (_loggingEnabled) {
      teardownBreadcrumbs();
      flush();
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this._caughtByReact = true;
    this.setState({ errorInfo: info });

    if (_loggingEnabled) {
      try {
        const replayId = captureErrorReplay();
        clientLogger.error(`React Error: ${error.message}`, {
          replay_id: replayId,
          stack: error.stack || null,
          componentStack: info?.componentStack || null,
          url: window.location.href,
        });
        flush();
      } catch {
        // Never let error logging crash the fallback UI
      }
    }

    if (!IS_DEV) {
      const count = getRefreshCount();
      if (count < MAX_AUTO_REFRESHES) {
        setRefreshCount(count + 1);
        setTimeout(() => window.location.reload(), 500);
        return;
      }
      clearRefreshCount();
    }
  }

  handleCopyError = () => {
    const { error, errorInfo } = this.state;
    const text = buildErrorReport(error, errorInfo);

    navigator.clipboard.writeText(text).then(
      () => {
        this.setState({ copied: true });
        if (this._copyTimer) clearTimeout(this._copyTimer);
        this._copyTimer = setTimeout(() => {
          this.setState({ copied: false });
          this._copyTimer = null;
        }, 2000);
      },
      () => {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          this.setState({ copied: true });
          if (this._copyTimer) clearTimeout(this._copyTimer);
          this._copyTimer = setTimeout(() => {
            this.setState({ copied: false });
            this._copyTimer = null;
          }, 2000);
        } catch {
          // Copy failed entirely — silently ignore.
        }
      }
    );
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const { error, errorInfo, copied } = this.state;
      const supportEmail = this.state.supportEmail || this.props.supportEmail || SUPPORT_EMAIL;
      const supportUrl = `mailto:${supportEmail}?subject=${encodeURIComponent('Application error report')}`;

      return (
        <div style={styles.backdrop}>
          <div style={styles.card(IS_DEV)}>

            <div style={styles.iconCircle(IS_DEV ? '#fef2f2' : '#fffbeb')}>
              {IS_DEV ? <BugIcon /> : <WarningIcon />}
            </div>

            {IS_DEV ? (
              <>
                <h2 style={styles.heading}>Unhandled error caught</h2>
                <p style={styles.devSubheading}>Development mode — error details below</p>
              </>
            ) : (
              <>
                <h2 style={styles.heading}>We couldn't complete your request</h2>
                <p style={styles.subheading}>
                  Something unexpected happened and we weren't able to load this page. The error
                  has been logged and our team has been notified.
                </p>
              </>
            )}

            {IS_DEV && error && (
              <div style={styles.errorContainer}>
                <div style={styles.errorHeader}>
                  {error.name ? `${error.name}: ` : ''}{error.message || String(error)}
                </div>
                {error.stack && (
                  <pre style={styles.errorStack}>{error.stack}</pre>
                )}
              </div>
            )}

            {IS_DEV && errorInfo?.componentStack && (
              <div style={styles.componentStackContainer}>
                <div style={styles.componentStackHeader}>Component stack</div>
                <pre style={styles.componentStack}>{errorInfo.componentStack}</pre>
              </div>
            )}

            {IS_DEV && error && (
              <div style={{ marginBottom: 8 }}>
                <button
                  onClick={this.handleCopyError}
                  style={styles.copyButton(copied)}
                  onMouseEnter={e => {
                    if (!copied) e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={e => {
                    if (!copied) e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                >
                  {copied ? <CheckIcon /> : <CopyIcon />}
                  {copied ? 'Copied to clipboard' : 'Copy error details'}
                </button>
              </div>
            )}

            <button
              onClick={() => {
                clearRefreshCount();
                window.location.reload();
              }}
              style={styles.button}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#1d4ed8';
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = '#2563eb';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.98)'; }}
              onMouseUp={e => { e.currentTarget.style.transform = 'scale(1.02)'; }}
            >
              <RefreshIcon />
              Refresh page
            </button>

            {!IS_DEV && (
              <p style={styles.supportText}>
                If this keeps happening,{' '}
                <a href={supportUrl} style={styles.supportLink}>
                  contact us at {supportEmail}
                </a>
              </p>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default LoggerBoundary;